import React, { useMemo } from 'react';
import AritoDataTables from '@/components/custom/arito/data-tables';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SearchColumnDef = {
  field: string;
  headerName?: string;
  flex?: number;
  width?: number;
  type?: any;
  renderCell?: (params: any) => ReactNode;
  renderHeader?: (params: any) => ReactNode;
  valueGetter?: (...args: any[]) => unknown;
  valueFormatter?: (...args: any[]) => unknown;
  sortable?: boolean;
  filterable?: boolean;
  disableColumnMenu?: boolean;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  editable?: boolean;
  checkboxSelection?: boolean;
  [key: string]: any;
};

export interface SearchTableProps {
  searchResults: any[];
  searchColumns?: SearchColumnDef[];
  searchResultLabelKey?: string;
  searchResultValueKey?: string;
  selectedResult?: any;
  selectedResults?: any[];
  multiSelect?: boolean;
  onResultClick: (result: any) => void;
  onResultDoubleClick: (result: any) => void;
  onConfirm?: (result: any | any[]) => void;
  onCancel?: () => void;
  showActionButtons?: boolean;
  onDragging?: (isDragging: boolean) => void;
  hideButtons?: boolean;
  getRowId?: (row: any) => string;
}

export const SearchTable: React.FC<SearchTableProps> = ({
  searchResults,
  searchColumns,
  searchResultValueKey,
  selectedResult,
  selectedResults = [],
  multiSelect = false,
  onResultClick,
  onResultDoubleClick,
  onConfirm,
  onCancel,
  showActionButtons = false,
  hideButtons = false,
  getRowId
}) => {
  const formattedColumns = useMemo<SearchColumnDef[]>(() => {
    if (!searchColumns) return [];

    return searchColumns.map(col => ({
      field: col.field,
      headerName: col.headerName || col.field,
      flex: col.flex || 1,
      width: col.width || 150,
      renderCell: col.checkboxSelection
        ? (params: any) => (
            <input
              type='checkbox'
              checked={params.value === true || params.value === 1}
              disabled
              className='size-4 cursor-default'
            />
          )
        : undefined,
      type: col.type || 'string'
    }));
  }, [searchColumns]);

  // Create table data structure for AritoDataTables
  const tableData = useMemo(
    () => [
      {
        name: '', // No tab name needed for search
        rows: searchResults,
        columns: formattedColumns
      }
    ],
    [searchResults, formattedColumns]
  );

  const handleConfirm = () => {
    if (onConfirm) {
      if (multiSelect) {
        onConfirm(selectedResults);
      } else if (selectedResult) {
        onConfirm(selectedResult);
      }
    }
  };

  return (
    <div className={cn('flex size-full flex-col border border-[#eaeaea] bg-white')}>
      <AritoDataTables
        tables={tableData}
        onRowClick={(params: { row: any; id: string }) => {
          onResultClick(params.row);

          const now = new Date().getTime();
          if (params.row.id === selectedResult?.id && now - (params.row._lastClickTime || 0) < 300) {
            onResultDoubleClick(params.row);
            if (onConfirm) {
              onConfirm(params.row);
            }
          }

          params.row._lastClickTime = now;
        }}
        selectedRowId={selectedResult?.id}
        getRowId={
          getRowId ||
          (row => {
            // Default implementation - use searchResultValueKey if provided, otherwise use code or generate an ID
            if (searchResultValueKey && row[searchResultValueKey] !== undefined) {
              return String(row[searchResultValueKey]);
            }
            return row.code !== undefined ? String(row.code) : String(Math.random());
          })
        }
      />

      {showActionButtons && !hideButtons && (
        <div className='flex justify-end gap-3 border-t border-[#eaeaea] bg-[#fafafa] p-3'>
          <button
            className={cn(
              'min-w-20 rounded bg-[rgba(15,118,110,0.9)] px-2.5 py-1 text-xs text-white hover:bg-[rgba(15,118,110,1)] disabled:bg-[#64748b] disabled:text-white',
              'transition-colors duration-150'
            )}
            onClick={handleConfirm}
            disabled={multiSelect ? selectedResults.length === 0 : !selectedResult}
          >
            Đồng ý
          </button>
          <button
            className='min-w-[80px] rounded border border-[rgba(15,118,110,0.9)] px-2.5 py-1 text-xs text-[rgba(15,118,110,0.9)] transition-colors duration-150 hover:border-[rgba(15,118,110,1)] hover:bg-[rgba(15,118,110,0.1)]'
            onClick={onCancel}
          >
            Hủy
          </button>
        </div>
      )}
    </div>
  );
};
